import { Directive, Input, ElementRef, AfterViewInit, inject, Renderer2, signal, WritableSignal, Output, EventEmitter } from '@angular/core';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';
import * as _ from 'lodash';
import { isScriptExist, script_error_text } from '@processo/consts/show_error';

@Directive({
  selector: '[customDateTime]',
  standalone: true,
  exportAs: 'customDateTime'
})
export class DateTimeDirective implements AfterViewInit {
  dateTimeFormat = 'dd-MM-yyyy HH:mm:ss';
  @Input() fieldProperties!: any;
  @Input() from!: any;
  @Input()
  set rowValue(val: any) {
    // Track if the value was originally undefined (field doesn't exist in entity_data)
    this._originalValueWasUndefined = val === undefined;
    this._rowValue = this.isValidDate(val) ? val : null;
  }
  get rowValue() {
    return this._rowValue;
  }
  private _rowValue: any;
  private _originalValueWasUndefined = false;

  isValidDate(d: any) {
    // Treat "0" (string or number) as an empty/invalid datetime value
    if (d === '0' || d === 0) return false;
    if (!d) return false;
    const date = new Date(d);
    return !isNaN(date.getTime());
  }
  @Output() valueChange = new EventEmitter<any>();
  private elementRef = inject(ElementRef<NzDatePickerComponent>);
  private fieldState: WritableSignal<any | null> = signal(null);
  private errorMessage = signal('');
  private renderer = inject(Renderer2);
  
  ngAfterViewInit() {
    this.initializeFieldState();
    this.dateTimeFormat = this.fieldProperties.dateTimeFormat || 'dd-MM-yyyy HH:mm:ss';
  }
  private initializeFieldState() {
    this.fieldState.set(this.fieldProperties);
    // Only apply default datetime if the field exists but is null or empty string
    // Don't apply default if field was originally undefined (doesn't exist in entity_data)
    // Note: The setter converts undefined to null, so we track the original value
    if (
      !this._originalValueWasUndefined &&
      (this.rowValue === null || this.rowValue === '') && 
      this.fieldState()?.default === 'Current_DateTime'
    ) {
      this.rowValue = new Date().toISOString();
    }
  }


  disabledDate = (current: Date): boolean => {
    const days: { [key: string]: number } = {
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
      Sun: 0,
    };
    const allowedDays: number[] = [];
    if (!this.fieldState() || !this.fieldState()?.allow_days?.length) {
      return false;
    }
    for (const day of this.fieldState()?.allow_days || []) {
      if (days[day] !== undefined) {
        allowedDays.push(days[day]);
      }
    }
    return !allowedDays.includes(current.getDay());
  };
  runValidator() {
    this.errorMessage.set(this.runValidation(this.rowValue));
    if (this.errorMessage()) {
      this.showErrorOverlay();
    } else {
      this.hideErrorOverlay();
    }
  }
  isScriptExist = isScriptExist;
  runValidation(value: string): string {
    const field = this.fieldState();
    if (!field) return '';
    let defaultValue;
    if (value && isScriptExist(value)) {
      return script_error_text;
    }
    if (field.mandatory && !value) {
      return `${field.label} is mandatory`;
    }
    return '';
  }
  showErrorOverlay() {
    const parentElement = this.elementRef.nativeElement.parentElement.parentElement;
    const existingErrorElement = parentElement.querySelector('.error-message');
    if (existingErrorElement) {
      this.renderer.removeChild(parentElement, existingErrorElement);
    }
    const errorElement = this.renderer.createElement('div');
    this.renderer.addClass(errorElement, 'error-message');
    this.renderer.setStyle(errorElement, 'color', 'red');
    this.renderer.setStyle(errorElement, 'font-size', '11px');
    if (this.from !== 'table') {
      this.renderer.setStyle(errorElement, 'text-align', 'left');
    }
    this.renderer.setProperty(errorElement, 'innerHTML', this.errorMessage());
    this.renderer.appendChild(parentElement, errorElement);
  }
  hideErrorOverlay() {
    const parentElement = this.elementRef.nativeElement.parentElement.parentElement;
    const errorElement = parentElement.querySelector('.error-message');
    if (errorElement) {
      this.renderer.removeChild(parentElement, errorElement);
    }
  }
  dateValueChange(e: any) {
    try {
      // Handle null or invalid date values
      if (!e) {
        this.rowValue = '';
        this.errorMessage.set(this.runValidation(''));
        this.hideErrorOverlay();
        this.valueChange.emit({ value: '' });
        return;
      }

      // Format the date as YYYY-MM-DD HH:mm:ss instead of ISO format
      const date = new Date(e);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        this.rowValue = '';
        this.errorMessage.set(this.runValidation(''));
        this.hideErrorOverlay();
        this.valueChange.emit({ value: '' });
        return;
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      
      this.errorMessage.set(this.runValidation(formattedDateTime));
      this._rowValue = formattedDateTime;
      this._originalValueWasUndefined = false; // Field now has a value
      if (this.errorMessage()) {
        this.showErrorOverlay();
      } else {
        this.hideErrorOverlay();
        this.valueChange.emit({ value: formattedDateTime });
      }
    } catch (error) {
      console.error('Error in dateValueChange:', error);
      this.rowValue = '';
      this.errorMessage.set(this.runValidation(''));
      this.hideErrorOverlay();
      this.valueChange.emit({ value: '' });
    }
  }
} 